import { Pipe, PipeTransform } from '@angular/core';
import { CommonService } from '../../core/services/common.service';
import { TranslateService } from '@ngx-translate/core';

@Pipe({
  name: 'addressTranslate',
  standalone: false
})
export class AddressTranslatePipe implements PipeTransform {

  constructor(
    private commonService: CommonService,
    private translateService: TranslateService
  ) { }

  transform(address: string, mode: AddressTranslateMode = AddressTranslateMode.FullAddress): Promise<string> {
    if (!address) return Promise.resolve('');

    switch (mode) {
      case AddressTranslateMode.Countries: return this.translateEntities(address, 'country');
      case AddressTranslateMode.States: return this.translateEntities(address, 'state');
      case AddressTranslateMode.Cities: return this.translateEntities(address, 'city');
      case AddressTranslateMode.Districts: return this.translateEntities(address, 'district');
      default: return this.translateAddressAsync(address);
    }
  }

  private async translateAddressAsync(address: string): Promise<string> {
    try {
      const cleanAddress = address.replace(/\.$/, '').trim();
      const parts = cleanAddress.split(',').map(p => p.trim());

      if (parts.length < 3) return address;

      const countryName = parts.pop()!;
      const stateName = parts.pop()!;
      const cityName = parts.pop()!;

      const separator = this.getSeparator();
      const freeText = parts.join(separator);

      const [country, state, city] = await Promise.all([
        this.commonService.getCountryByNameLocalAsync(countryName),
        this.commonService.getStateByNameLocalAsync(stateName, countryName),
        this.commonService.getCityByNameLocalAsync(cityName, stateName, countryName)
      ]);

      const translated = [
        freeText,
        city?.displayName || cityName,
        state?.displayName || stateName,
        country?.displayName || countryName
      ].filter(Boolean).join(separator);

      return translated + '.';
    } catch (error) {
      console.error('Error formatting address with lookup:', error);
      return address;
    }
  }

  private async translateEntities(input: string, type: 'country' | 'state' | 'city' | 'district'): Promise<string> {
    const separator = this.getSeparator();
    const parts = input.replace(/\.$/, '').split(',').map(p => p.trim());

    const lookups = parts.map(async (name) => {
      switch (type) {
        case 'country':
          return (await this.commonService.getCountryByNameLocalAsync(name))?.displayName || name;
        case 'state':
          return (await this.commonService.getStateByNameLocalAsync(name))?.displayName || name;
        case 'city':
          return (await this.commonService.getCityByNameLocalAsync(name))?.displayName || name;
        case 'district':
          return (await this.commonService.getDistrictByNameLocalAsync(name))?.displayName || name;
      }
    });

    const translatedParts = await Promise.all(lookups);
    return translatedParts.filter(Boolean).join(separator);
  }

  private getSeparator(): string {
    return this.translateService.currentLang === 'en' ? ', ' : '، ';
  }
}

export enum AddressTranslateMode {
  FullAddress,
  Countries,
  States,
  Cities,
  Districts
}
